import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import "./BodhiMessage.css";

/* ── Icons ─────────────────────────────────────────────────────────────── */
const SuccessIcon = () => (
	<svg
		viewBox="0 0 20 20"
		width="18"
		height="18"
		fill="none"
		aria-hidden="true"
	>
		<circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.16" />
		<path
			d="M6 10.5l2.5 2.5L14 7.5"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);
const ErrorIcon = () => (
	<svg
		viewBox="0 0 20 20"
		width="18"
		height="18"
		fill="none"
		aria-hidden="true"
	>
		<circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.16" />
		<path
			d="M7 7l6 6M13 7l-6 6"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
		/>
	</svg>
);
const InfoIcon = () => (
	<svg
		viewBox="0 0 20 20"
		width="18"
		height="18"
		fill="none"
		aria-hidden="true"
	>
		<circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.16" />
		<path
			d="M10 9v4.5M10 6.5h.01"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
		/>
	</svg>
);
const WarningIcon = () => (
	<svg
		viewBox="0 0 20 20"
		width="18"
		height="18"
		fill="none"
		aria-hidden="true"
	>
		<circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.16" />
		<path
			d="M10 6v4.5M10 13.5h.01"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
		/>
	</svg>
);

const ICONS = {
	success: SuccessIcon,
	error: ErrorIcon,
	info: InfoIcon,
	warning: WarningIcon,
};

/**
 * BodhiMessage — a single branded toast. Presentational; render it directly
 * (e.g. in the brand book) or let the `bodhiMessage` API manage a stack of them.
 */
export const BodhiMessage = ({
	type = "info",
	content,
	className = "",
	...rest
}) => {
	const Icon = ICONS[type] || InfoIcon;
	return (
		<div
			className={`bodhi-msg bodhi-msg-${type} ${className}`.trim()}
			role="status"
			{...rest}
		>
			<span className="bodhi-msg-icon">
				<Icon />
			</span>
			<span className="bodhi-msg-content">{content}</span>
		</div>
	);
};

/* ── Imperative toast store (vanilla pub/sub — no extra deps) ───────────── */
const store = {
	toasts: [],
	listeners: new Set(),
	seq: 0,
	subscribe(fn) {
		this.listeners.add(fn);
		fn(this.toasts);
		return () => this.listeners.delete(fn);
	},
	emit() {
		for (const l of this.listeners) l(this.toasts);
	},
	add(type, content, duration = 3000) {
		const id = ++this.seq;
		this.toasts = [...this.toasts, { id, type, content }];
		this.emit();
		if (duration > 0) setTimeout(() => this.remove(id), duration);
		return id;
	},
	remove(id) {
		this.toasts = this.toasts.filter((t) => t.id !== id);
		this.emit();
	},
};

const MessageStack = () => {
	const [items, setItems] = useState(store.toasts);
	useEffect(() => store.subscribe(setItems), []);
	if (typeof document === "undefined") return null;
	return createPortal(
		<div className="bodhi-msg-stack">
			{items.map((t) => (
				<BodhiMessage key={t.id} type={t.type} content={t.content} />
			))}
		</div>,
		document.body,
	);
};

// Lazily mount a single container the first time a toast is shown, so callers
// can use `bodhiMessage.success(...)` without wiring up a provider.
let mounted = false;
const ensureContainer = () => {
	if (mounted || typeof document === "undefined") return;
	mounted = true;
	const host = document.createElement("div");
	host.setAttribute("data-bodhi-message-root", "");
	document.body.appendChild(host);
	createRoot(host).render(<MessageStack />);
};

const show = (type) => (content, duration) => {
	ensureContainer();
	return store.add(type, content, duration);
};

/**
 * Imperative toast API — a drop-in replacement for antd's `message`.
 *   bodhiMessage.success("Saved");
 *   bodhiMessage.error("Something went wrong", 5000);
 */
export const bodhiMessage = {
	success: show("success"),
	error: show("error"),
	info: show("info"),
	warning: show("warning"),
};

export default bodhiMessage;
